// Auth API service - connects to the auth-api microservice
const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Generate a simple device fingerprint based on browser characteristics
export async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    (navigator as unknown as { deviceMemory?: number }).deviceMemory || 'unknown',
  ];
  const fingerprint = components.join('|');
  // Use SubtleCrypto to hash the fingerprint
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Get a human-readable device name
export function getDeviceName(): string {
  const ua = navigator.userAgent;
  let device = 'Unknown Device';
  
  if (/iPhone/.test(ua)) device = 'iPhone';
  else if (/iPad/.test(ua)) device = 'iPad';
  else if (/Android/.test(ua)) device = 'Android Device';
  else if (/Mac/.test(ua)) device = 'Mac';
  else if (/Windows/.test(ua)) device = 'Windows PC';
  else if (/Linux/.test(ua)) device = 'Linux PC';
  
  // Add browser
  if (/Chrome/.test(ua) && !/Edge/.test(ua)) device += ' - Chrome';
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) device += ' - Safari';
  else if (/Firefox/.test(ua)) device += ' - Firefox';
  else if (/Edge/.test(ua)) device += ' - Edge';
  
  return device;
}

interface ApiError {
  error: string;
}

async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || "Request failed");
  }

  return data as T;
}

// Types
export interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  permissions: Record<string, unknown>;
  is_system_role: boolean;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  status: string;
  mfa_enabled: boolean;
  mfa_method?: string;
  email_verified: boolean;
  created_at: string;
  last_login_at?: string;
  roles?: Role[];
}

// Role name constants
export const ROLE_ADMIN = "TradeCraft_Admin";
export const ROLE_USER = "TradeCraft_User";
export const ROLE_DEMO_USER = "TradeCraft_Demo_User";
export const ROLE_API_USER = "TradeCraft_Api_User";

// Helper function to check if user has a specific role
export function hasRole(user: User | null, roleName: string): boolean {
  if (!user || !user.roles) return false;
  return user.roles.some((role) => role.name === roleName);
}

// Helper function to check if user is admin
export function isAdmin(user: User | null): boolean {
  return hasRole(user, ROLE_ADMIN);
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  token_type: string;
  user: User;
}

export interface MFARequiredResponse {
  mfa_required: boolean;
  session_id: string;
  mfa_method: string;
  access_token: string;
  refresh_token: string;
  expires_at: number;
  token_type: string;
}

export interface RegisterResponse {
  message: string;
  user_id: string;
}

// Auth APIs
export async function login(
  email: string,
  password: string,
  deviceFingerprint?: string,
): Promise<AuthResponse | MFARequiredResponse> {
  return authRequest<AuthResponse | MFARequiredResponse>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ 
      email, 
      password,
      device_fingerprint: deviceFingerprint,
    }),
  });
}

export async function register(
  email: string,
  password: string,
  displayName: string,
): Promise<RegisterResponse> {
  return authRequest<RegisterResponse>("/api/v1/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, display_name: displayName }),
  });
}

export async function verifyMFA(
  sessionId: string,
  code: string,
  trustDevice?: boolean,
  deviceFingerprint?: string,
  deviceName?: string,
): Promise<AuthResponse> {
  return authRequest<AuthResponse>("/api/v1/auth/mfa/verify", {
    method: "POST",
    body: JSON.stringify({ 
      session_id: sessionId, 
      code,
      trust_device: trustDevice,
      device_fingerprint: deviceFingerprint,
      device_name: deviceName,
    }),
  });
}

export async function logout(): Promise<void> {
  await authRequest<{ message: string }>("/api/v1/auth/logout", {
    method: "POST",
  });
}

export async function refreshToken(
  refreshToken: string,
): Promise<AuthResponse> {
  return authRequest<AuthResponse>("/api/v1/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

export async function forgotPassword(
  email: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  userId: string,
  code: string,
  newPassword: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ user_id: userId, code, new_password: newPassword }),
  });
}

export async function verifyEmail(
  userId: string,
  code: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ user_id: userId, code }),
  });
}

export async function getMe(): Promise<User> {
  return authRequest<User>("/api/v1/users/me");
}

export async function updateProfile(
  displayName?: string,
  avatarUrl?: string,
): Promise<User> {
  return authRequest<User>("/api/v1/users/me", {
    method: "PUT",
    body: JSON.stringify({ display_name: displayName, avatar_url: avatarUrl }),
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/users/me/password", {
    method: "PUT",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

// MFA APIs
export interface MFAEnableResponse {
  secret?: string;
  qr_code?: string;
  backup_codes: string[];
}

export async function enableMFA(
  method: "totp" | "email",
): Promise<MFAEnableResponse> {
  return authRequest<MFAEnableResponse>("/api/v1/auth/mfa/enable", {
    method: "POST",
    body: JSON.stringify({ method }),
  });
}

export async function disableMFA(
  password: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/mfa/disable", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function regenerateBackupCodes(
  password: string,
): Promise<{ backup_codes: string[]; message: string }> {
  return authRequest<{ backup_codes: string[]; message: string }>(
    "/api/v1/auth/mfa/regenerate-backup-codes",
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  );
}

export async function resendVerificationEmail(
  email: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resendMFACode(
  sessionId: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>("/api/v1/auth/mfa/resend", {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId }),
  });
}

// Helper to check if response is MFA required
export function isMFARequired(
  response: AuthResponse | MFARequiredResponse,
): response is MFARequiredResponse {
  return "mfa_required" in response && response.mfa_required === true;
}
